import { useCallback, useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { VoiceRecorder, voiceRecordingSupported, type VoiceAttachment } from "./VoiceRecorder";
import { useVoiceMode } from "./voiceMode";

// LDF: Spracheingabe im Browser (Web Speech API, in Safari und Chrome verfügbar).
// Der Text wird Stück für Stück übergeben; der Browser schickt die Audiodaten dazu an den Spracherkenner von Apple bzw. Google.

type RecognitionResult = { isFinal: boolean; 0: { transcript: string } };
type RecognitionEvent = { resultIndex: number; results: ArrayLike<RecognitionResult> };
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: RecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start: () => void;
  stop: () => void;
};

const getRecognitionClass = (): (new () => Recognition) | null => {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

export const dictationSupported = () => !!getRecognitionClass();

/**
 * Laufende Spracherkennung. onFinal bekommt fertige Sätze, interim zeigt den gerade gesprochenen Teil.
 * Bricht der Browser nach einer Pause ab, wird automatisch neu gestartet, solange listening aktiv ist.
 */
export const useDictation = (onFinal: (text: string) => void) => {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<Recognition | null>(null);
  const activeRef = useRef(false);
  const restartsRef = useRef<number[]>([]);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  const stop = useCallback(() => {
    activeRef.current = false;
    setListening(false);
    setInterim("");
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {
        // bereits beendet
      }
      // iOS reagiert auf stop() manchmal nicht – dann hart abbrechen
      setTimeout(() => {
        try {
          (recognition as Recognition & { abort?: () => void }).abort?.();
        } catch {
          // bereits beendet
        }
      }, 400);
    }
  }, []);

  const start = useCallback(() => {
    const Recognition = getRecognitionClass();
    if (!Recognition) {
      setError("Spracheingabe wird in diesem Browser nicht unterstützt. Alternative: macOS-Diktat (zweimal Fn drücken).");
      return;
    }
    setError(null);
    const recognition = new Recognition();
    recognition.lang = "de-DE";
    // iOS/iPadOS: „continuous“ führt dort zu ständigen Abbrüchen und Neustarts, die die Oberfläche blockieren
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    recognition.continuous = !isIos;
    recognition.interimResults = true;
    recognition.onresult = (e) => {
      let live = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        if (result.isFinal) onFinalRef.current(result[0].transcript.trim());
        else live += result[0].transcript;
      }
      setInterim(live);
    };
    recognition.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setError("Mikrofon nicht freigegeben. In Safari: Einstellungen → Websites → Mikrofon → crm.lovelydayflowers.de erlauben.");
        stop();
      }
    };
    recognition.onend = () => {
      if (!activeRef.current) return;
      // Neustart nach Sprechpause – aber höchstens 3× in 10 Sekunden, sonst beenden (verhindert Endlosschleife)
      const now = Date.now();
      restartsRef.current = [...restartsRef.current.filter((t) => now - t < 10000), now];
      if (restartsRef.current.length > 3) return stop();
      setTimeout(() => {
        if (!activeRef.current) return;
        try {
          recognition.start();
        } catch {
          stop();
        }
      }, 250);
    };
    recognitionRef.current = recognition;
    activeRef.current = true;
    restartsRef.current = [];
    setListening(true);
    try {
      recognition.start();
    } catch {
      stop();
    }
  }, [stop]);

  useEffect(() => () => {
    activeRef.current = false;
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {
        // bereits beendet
      }
    }
  }, []);

  return { listening, interim, error, start, stop };
};

// Text sinnvoll anhängen (Leerzeichen, Großschreibung am Satzanfang)
export const appendDictated = (current: string, addition: string) => {
  if (!addition) return current;
  const text = addition.charAt(0).toUpperCase() + addition.slice(1);
  if (!current.trim()) return text;
  return /[.!?:]\s*$/.test(current) ? `${current.trimEnd()} ${text}` : `${current.trimEnd()} ${addition}`;
};

/**
 * LDF: Sprach-Knopf wie bei WhatsApp – grüner runder Mikrofon-Knopf.
 * Kurz tippen = Aufnahme an, nochmal tippen = aus. Gedrückt halten = spricht, solange gehalten wird.
 */
export const VoiceButton = ({
  listening,
  onStart,
  onStop,
  size = "md",
  title = "Sprechen",
}: {
  listening: boolean;
  onStart: () => void;
  onStop: () => void;
  size?: "sm" | "md" | "lg";
  title?: string;
}) => {
  const pressedAt = useRef<number | null>(null);
  const wasListening = useRef(false);
  const dim = size === "lg" ? "h-14 w-14" : size === "sm" ? "h-9 w-9" : "h-11 w-11";
  const icon = size === "lg" ? "h-7 w-7" : size === "sm" ? "h-4 w-4" : "h-5 w-5";
  return (
    <button
      type="button"
      title={listening ? "Aufnahme beenden" : `${title} – tippen oder gedrückt halten`}
      aria-label={listening ? "Aufnahme beenden" : title}
      aria-pressed={listening}
      onPointerDown={(e) => {
        e.preventDefault();
        pressedAt.current = Date.now();
        wasListening.current = listening;
        if (!listening) onStart();
      }}
      onPointerUp={() => {
        const held = pressedAt.current != null && Date.now() - pressedAt.current > 500;
        pressedAt.current = null;
        // gehalten → beim Loslassen beenden; kurzer Tipp auf laufende Aufnahme → beenden
        if (held || wasListening.current) onStop();
      }}
      onPointerCancel={() => {
        pressedAt.current = null;
      }}
      onContextMenu={(e) => e.preventDefault()}
      className={`relative inline-flex shrink-0 select-none items-center justify-center rounded-full text-white shadow-md transition-colors cursor-pointer [-webkit-touch-callout:none] ${dim} ${
        listening ? "bg-red-600" : "bg-[#25A244] hover:bg-[#1f8f3b] active:bg-[#1a7a32]"
      }`}
    >
      {listening && <span className="absolute inset-0 animate-ping rounded-full bg-red-500/40" />}
      <Mic className={`relative ${icon}`} />
    </button>
  );
};

/**
 * Mikrofon-Knopf überall im CRM. Wenn der Browser Aufnahmen kann: Sprachnachricht wie bei WhatsApp,
 * verschriftlicht mit Whisper auf dem Mac mini. Sonst Fallback auf die Spracherkennung des Browsers.
 */
export const MicButton = ({
  onText,
  className,
  title = "Diktieren",
  size = "sm",
  hints,
  onAudio,
}: {
  onText: (text: string) => void;
  className?: string;
  title?: string;
  size?: "sm" | "md" | "lg";
  hints?: string;
  onAudio?: (attachment: VoiceAttachment) => void;
}) => {
  const [active, setActive] = useState(false);
  const [mode] = useVoiceMode();
  if (voiceRecordingSupported() && !(mode === "geraet" && dictationSupported())) {
    return (
      <span className={`inline-flex items-center ${active ? "min-w-0 flex-1" : ""} ${className ?? ""}`}>
        <VoiceRecorder onText={onText} onAudio={onAudio} hints={hints} title={title} size={size === "lg" ? "lg" : "md"} onActiveChange={setActive} />
      </span>
    );
  }
  return <BrowserMicButton onText={onText} className={className} title={title} size={size} />;
};

const BrowserMicButton = ({
  onText,
  className,
  title,
  size,
}: {
  onText: (text: string) => void;
  className?: string;
  title: string;
  size: "sm" | "md" | "lg";
}) => {
  const { listening, interim, error, start, stop } = useDictation(onText);
  return (
    <span className={`inline-flex items-center gap-2 ${className ?? ""}`}>
      {(listening || error) && (
        <span className={`max-w-56 truncate text-xs ${error ? "text-destructive" : "italic text-muted-foreground"}`}>
          {error ?? (interim ? `${interim} …` : "Hört zu …")}
        </span>
      )}
      <VoiceButton listening={listening} onStart={start} onStop={stop} size={size} title={title} />
    </span>
  );
};
